<template>
  <div class="bulk-rate-assign">

    <!-- Selection count bar -->
    <div class="bulk-rate-assign__bar">
      <span class="bulk-rate-assign__count">{{ count }} selected</span>

      <!-- Mixed-org guard -->
      <el-alert
        v-if="!sameOrg"
        type="info"
        :closable="false"
        show-icon
        :title="'Bulk rate change needs a single telco — ' + selectedOrgIds.length + ' selected. Narrow your selection to one telco to continue.'"
        class="bulk-rate-assign__org-alert" />

      <template v-if="sameOrg || selectAllMatching">
        <!-- Mode toggle: override enabled in select-all mode (XOL-340 / API PR #170, verified live) -->
        <el-radio-group
          v-model="applyMode"
          size="small"
          :disabled="countingOverrides || applying"
          class="bulk-rate-assign__mode"
          aria-label="Apply mode">
          <el-radio-button label="rate">Assign sharer rate</el-radio-button>
          <!-- Wrap in span so el-tooltip fires while the control is gated.
               Tooltip only shows while gated (overrideAvailableInSelectAll=false). -->
          <el-tooltip
            :disabled="!(selectAllMatching && !overrideAvailableInSelectAll)"
            effect="dark"
            placement="top"
            content="Manual override across all matching licensees is coming soon.">
            <span>
              <el-radio-button
                label="override"
                :disabled="selectAllMatching && !overrideAvailableInSelectAll"
                :aria-disabled="selectAllMatching && !overrideAvailableInSelectAll">
                Set manual override
              </el-radio-button>
            </span>
          </el-tooltip>
        </el-radio-group>

        <!-- Rate mode controls -->
        <template v-if="isRateMode">
          <select-sharer-rates
            ref="rateSelect"
            :sharer-id="orgId"
            v-model="rateId"
            :disabled="countingOverrides || applying"
            :no-form="true"
            placeholder="Choose a rate to apply…"
            class="bulk-rate-assign__picker" />

          <!-- Version picker. In select-all mode it's enabled once the version-pin
               API (XOL-340 / #170) is live; the tooltip only shows while gated. -->
          <el-tooltip
            :disabled="!(selectAllMatching && !versionPinAvailable)"
            effect="dark"
            placement="top"
            content="Pinning a version across all matching licensees is coming soon.">
            <span class="bulk-rate-assign__picker bulk-rate-assign__version">
              <select-rate-versions
                :versions="rateVersions"
                :value="versionId"
                :loading="loadingVersions"
                :disabled="(selectAllMatching && !versionPinAvailable) || !rateId || countingOverrides || applying"
                :aria-disabled="selectAllMatching && !versionPinAvailable"
                clearable
                placeholder="Latest (published)"
                @input="onApplyVersionInput"
                style="width: 100%" />
            </span>
          </el-tooltip>

          <!-- XOL-653 part B — "Also clear rate overrides": ONE atomic flag
               (clear_override) on the same write that assigns the rate, never a second
               request. Off by default. If a capability constant is ever set to false, the box
               is disabled and unticked, with the existing "coming soon" tooltip pattern. -->
          <el-tooltip
            :disabled="!clearOverrideGated"
            effect="dark"
            placement="top"
            :content="clearOverrideGateText">
            <span class="bulk-rate-assign__clear-override">
              <el-checkbox
                v-model="clearOverride"
                :disabled="clearOverrideGated || applying || countingOverrides"
                :aria-disabled="clearOverrideGated">
                Also clear rate overrides
              </el-checkbox>
              <help-tip
                v-if="!clearOverrideGated"
                content="Removes the manual fee from licensees with a rate override, so they bill the new rate. Left unticked, they keep billing their manual fee." />
            </span>
          </el-tooltip>

          <!-- XOL-321 Phase B — apply-with-exclusions safety gate.
               Wrap Apply in a <span> + el-tooltip (mirrors the override/version
               "coming soon" pattern) so the tooltip fires on the disabled button.
               :disabled on the tooltip = don't show the tooltip unless
               exclusionsBlockApply is true (i.e. exclusions present + API not ready).
               aria-describedby wires to the tooltip's content id so assistive
               technology reads the reason while Apply is disabled. -->
          <el-tooltip
            :disabled="!exclusionsBlockApply"
            effect="dark"
            placement="top"
            :content="'De-selecting individual records is coming soon. Reset exclusions to apply to all ' + matchCount + ', or use per-page selection instead.'">
            <span>
              <el-button
                type="primary"
                :disabled="!canApply"
                :aria-disabled="!canApply"
                :loading="applying || countingOverrides"
                @click="confirmAndApply"
                class="bulk-rate-assign__apply-btn">
                Apply rate to {{ count }}
              </el-button>
            </span>
          </el-tooltip>
        </template>

        <!-- Override mode controls: available in both explicit and select-all modes (XOL-340 live) -->
        <template v-if="isOverrideMode">
          <currency-input
            v-model="overrideAmount"
            :no-form="true"
            label=""
            placeholder="Manual fee amount"
            class="bulk-rate-assign__picker" />

          <el-button
            type="primary"
            :disabled="!canApply"
            :loading="applying"
            @click="confirmAndApply"
            class="bulk-rate-assign__apply-btn">
            Set override on {{ count }}
          </el-button>

          <el-button
            type="danger"
            plain
            :disabled="!canClear"
            :loading="applying"
            @click="confirmAndClear"
            class="bulk-rate-assign__apply-btn">
            Clear override on {{ count }}
          </el-button>
        </template>
      </template>
    </div>

    <!-- Progress indicator: no row counter in select-all mode (async, unknown per-row state) -->
    <div v-if="applying || countingOverrides" class="bulk-rate-assign__progress" role="status">
      <template v-if="countingOverrides">Checking rate overrides…</template>
      <template v-else-if="selectAllMatching">Queuing…</template>
      <template v-else>Applying {{ doneCount }} of {{ count }}…</template>
    </div>

    <!-- Result alerts -->
    <template v-if="result">
      <!-- Success / queued alert (both bulk-filter and fan-out modes) -->
      <el-alert
        v-if="isBulkFilterResult || result.applied > 0"
        type="success"
        :closable="false"
        show-icon
        :title="successTitle"
        class="bulk-rate-assign__result-alert" />

      <!-- Error alerts (bulk-filter mode: 422 or generic network/5xx) -->
      <el-alert
        v-if="isBulkFilterError"
        type="error"
        :closable="false"
        show-icon
        :title="result.error"
        class="bulk-rate-assign__result-alert" />

      <!-- Defensive failure list for bulk-filter mode (202 with non-empty failures, normally []) -->
      <el-alert
        v-if="isBulkFilterResult && result.failures && result.failures.length"
        type="warning"
        :closable="false"
        show-icon
        :title="bulkFilterFailureTitle"
        class="bulk-rate-assign__result-alert">
        <div slot="default" class="bulk-rate-assign__failure-list">
          <div
            v-for="(f, idx) in result.failures.slice(0, 50)"
            :key="bulkFilterFailureKey(f, idx)"
            class="bulk-rate-assign__failure-item">
            Licensee {{ f.licensee_id }} — {{ f.reason }}
          </div>
        </div>
      </el-alert>

      <!-- Fan-out failure list (explicit per-row results) -->
      <el-alert
        v-if="result.failed && !isBulkFilterResult"
        type="error"
        :closable="false"
        show-icon
        :title="fanOutFailureTitle"
        class="bulk-rate-assign__result-alert">
        <div slot="default" class="bulk-rate-assign__failure-list">
          <div
            v-for="(f, idx) in result.failures.slice(0, 50)"
            :key="idx"
            class="bulk-rate-assign__failure-item">
            {{ (f.row.organisation && f.row.organisation.name) }} — {{ (f.row.site && f.row.site.siteId) }}
          </div>
        </div>
      </el-alert>
    </template>

  </div>
</template>

<script>

import axios from '@/lib/axiosClient'
import NumberMixins from '@/mixins/NumberMixins'
import SelectSharerRates from '@/components/atoms/selects/SelectSharerRates'
import SelectRateVersions from '@/components/atoms/selects/SelectRateVersions'
import CurrencyInput from '@/components/atoms/inputs/CurrencyInput'
import { CLEAR_OVERRIDE_KEY, OVERRIDES_TO_CLEAR_KEY, cloneParams, lockedCountParams } from '@/lib/bulkRateFilters'

// Why the override gets removed, repeated in every confirm sentence.
var BILL_NEW_RATE = 'the new rate will be billed from the next fee recalculation.'

// Same ids in the same order. A reorder (onSelectionChange can re-push the page ids) reads as
// "changed" and aborts; that is the safe direction.
function sameIds (a, b) {
  if (a.length !== b.length) return false
  for (var i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function isLocked (row) {
  return !!row && (row.rate_locked === true || row.rate_locked === 1 || row.rate_locked === '1')
}

export default {
  name: 'bulk-rate-assign',

  // userName() + getEndpoint() are global app-wide mixin methods (AccessMixins),
  // not PostMixins — so no local mixin is needed here. NumberMixins added for
  // formatMoney in override confirm body (Phase 2).
  mixins: [NumberMixins],

  components: {
    SelectSharerRates,
    SelectRateVersions,
    CurrencyInput
  },

  props: {
    // Scopes the rate picker; when null the picker falls back to the single
    // selected org if sameOrg, otherwise disabled.
    organisationId: {
      type: Number,
      default: null
    },
    // Selected audit rows. Each is expected to carry:
    //   licensee_id, organisation: {id, name}, site: {id, siteId, siteName}
    licenseeRows: {
      type: Array,
      default: function () { return [] }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Phase B: total matched count from the search (XolasTable @total-changed).
    // In select-all mode, count shows this value instead of licenseeRows.length.
    matchCount: {
      type: Number,
      default: null
    },
    // Phase B: true when the user has clicked "Select all N matching" — apply
    // routes through POST /licensees/bulk-rate filter-mode (not fan-out).
    selectAllMatching: {
      type: Boolean,
      default: false
    },
    // Phase B: the searchParams object from BulkRateChange, used as the filter
    // body for POST /licensees/bulk-rate filter-mode.
    filter: {
      type: Object,
      default: function () { return {} }
    },
    // XOL-321 Phase B — select-all-with-exclusions.
    // excludedIds: ids of licensees de-selected while in select-all mode.
    // count subtracts these from matchCount so the display and confirm copy
    // reflect the actual post-exclusion target set.
    // Apply is DISABLED when selectAllMatching && excludedIds.length > 0 &&
    // !excludeIdsApiAvailable (safety gate until brave-kestrel ships exclude_ids).
    excludedIds: {
      type: Array,
      default: function () { return [] }
    }
  },

  data: function () {
    return {
      applyMode: 'rate',
      rateId: null,
      versionId: null,
      rateVersions: [],
      loadingVersions: false,
      overrideAmount: null,
      applying: false,
      doneCount: 0,
      result: null,
      // XOL-653 part B — "Also clear rate overrides". Off by default; an explicit,
      // per-apply choice (it unticks after a successful apply and on a mode change).
      clearOverride: false,
      // True while the select-all pre-flight (how many matching licensees are overridden) runs.
      countingOverrides: false
    }
  },

  computed: {
    // In select-all mode, count reflects the matched set minus any exclusions;
    // guarded with Math.max(0,…) so it never goes negative.
    // In explicit (fan-out) mode, count is the number of checked rows.
    count: function () {
      if (this.selectAllMatching && this.matchCount !== null) {
        return Math.max(0, this.matchCount - this.excludedIds.length)
      }
      return this.licenseeRows.length
    },

    // ENABLED — bulk-rate filter-mode honours mode:override / mode:override-clear
    // (brave-kestrel XOL-340 / #170), verified live: org 982 {mode:override,currentRate}
    // → 202 {queued:299}; {mode:override-clear} → 202 {queued:300}.
    // MUST ship paired with the XOL-340 API merge to main.
    overrideAvailableInSelectAll: function () {
      return true
    },

    // XOL-321 Phase B — apply-with-exclusions safety gate.
    // brave-kestrel: flip to true once POST /licensees/bulk-rate accepts exclude_ids[].
    // While false, Apply is disabled whenever the user has de-selected rows in
    // select-all mode — we must NOT POST a filter-mode request that silently
    // ignores exclusions (money-path correctness bug).
    excludeIdsApiAvailable: function () {
      // ENABLED — POST /licensees/bulk-rate filter-mode honours `exclude_ids[]`
      // (brave-kestrel XOL-347, verified live: org 982 select-all 300 + exclude 3
      // → 202 {queued:297}). This SPA enable MUST ship paired with the XOL-347 API
      // merge to main (they are the two halves of the same feature); do not deploy
      // the SPA ahead of that API change, or exclusions would be silently dropped.
      return true
    },

    // True when the bulk endpoint honours `rate_version_id` in filter-mode (the
    // version pin across all matching). ENABLED — brave-kestrel XOL-340 / #170,
    // verified live: rate 8697 + rate_version_id 980 → 202; a version of another
    // rate → 422 "Rate version does not belong to this rate." Like exclude_ids,
    // this SPA enable MUST ship paired with the XOL-340 API merge to main.
    versionPinAvailable: function () {
      return true
    },

    // XOL-653 part B — capability constants, the same pattern as the two above.
    // ENABLED (Carlos, 2026-10-19: the feature ships in this PR). The atomic
    // `clear_override` flag ships in xolas-tms-api PR #293:
    //   clearOverrideAvailable             — on PUT /licensees/{id} (ticked rows)
    //   clearOverrideAvailableInSelectAll  — on POST /licensees/bulk-rate mode=rate
    // The API MUST deploy before this SPA release. If either is ever set back to
    // false, the checkbox is disabled and forced unticked, so a flag the server
    // would ignore is never sent.
    clearOverrideAvailable: function () {
      return true
    },

    clearOverrideAvailableInSelectAll: function () {
      return true
    },

    // The checkbox is gated (disabled + unticked) for the CURRENT selection mode.
    clearOverrideGated: function () {
      if (!this.clearOverrideAvailable) return true
      return !!(this.selectAllMatching && !this.clearOverrideAvailableInSelectAll)
    },

    clearOverrideGateText: function () {
      if (!this.clearOverrideAvailable) {
        return 'Clearing rate overrides while assigning a rate is coming soon.'
      }
      return 'Clearing rate overrides across all matching licensees is coming soon.'
    },

    // Selected rows that carry a manual override (explicit selection only).
    selectedLockedCount: function () {
      return this.licenseeRows.filter(isLocked).length
    },

    // True when the Apply button must be disabled AND the tooltip must be shown.
    // Distinct from canApply=false so the template can decide whether to render
    // the tooltip wrapper without duplicating the guard logic.
    exclusionsBlockApply: function () {
      return !!(this.selectAllMatching && this.excludedIds.length > 0 && !this.excludeIdsApiAvailable)
    },

    selectedOrgIds: function () {
      var seen = {}
      var ids = []
      this.licenseeRows.forEach(function (row) {
        var id = row.organisation && row.organisation.id
        if (id && !seen[id]) {
          seen[id] = true
          ids.push(id)
        }
      })
      return ids
    },

    sameOrg: function () {
      return this.selectedOrgIds.length <= 1
    },

    orgId: function () {
      return this.organisationId || this.selectedOrgIds[0] || null
    },

    canApply: function () {
      // XOL-321 Phase B — safety gate: NEVER allow apply when exclusions are
      // present and the exclude_ids API param is not yet available. This prevents
      // a filter-mode POST that would silently apply to the FULL matched set
      // (ignoring exclusions) — a money-path correctness bug.
      if (this.exclusionsBlockApply) return false

      // In select-all mode, sameOrg is inherent (the filter scopes to organisation_id)
      var base = this.count > 0 && !this.applying && !this.disabled
      if (!this.selectAllMatching) {
        base = base && this.sameOrg
      }
      // Override is now available in select-all mode (XOL-340 live).
      var effectiveMode = this.applyMode
      if (effectiveMode === 'rate') {
        return base && !!this.rateId
      }
      // override-set — NOT gated on overrideAmount: CurrencyInput only commits its
      // value on blur (@change), so if the button were disabled until the amount
      // is non-zero, the user's first click (which blurs the field) would land on a
      // still-disabled button and do nothing. Keep it enabled and validate the
      // amount in confirmAndApply (which runs after the blur commits the value).
      return base
    },

    canClear: function () {
      return this.count > 0 && this.sameOrg && !this.applying && !this.disabled
    },

    // Template helpers to avoid string comparisons with single quotes inside
    // double-quoted attribute values (causes vue-jest template compilation errors
    // when the vue-jest template compiler tokenizes attribute values).
    // Override is now reachable in select-all mode (XOL-340 live); the selectAllMatching
    // watcher still snaps applyMode='rate' on entry so rate is the default.
    isRateMode: function () {
      return this.applyMode === 'rate'
    },

    isOverrideMode: function () {
      return this.applyMode === 'override'
    },

    isBulkFilterResult: function () {
      return !!(this.result && this.result.mode === 'bulk-filter')
    },

    isBulkFilterError: function () {
      return !!(this.result && (this.result.mode === 'bulk-filter-error-422' || this.result.mode === 'bulk-filter-error'))
    },

    bulkFilterFailureTitle: function () {
      if (!this.result || !this.result.failures) return ''
      var n = this.result.failures.length
      return n + ' licensee' + (n === 1 ? '' : 's') + " couldn't be queued"
    },

    fanOutFailureTitle: function () {
      if (!this.result) return ''
      // The flag is atomic per row, so a failed row kept both its rate and its override.
      if (this.result.clearOverride) {
        return this.result.failed + " couldn't be applied — their rate and any override are unchanged"
      }
      return this.result.failed + " couldn't be applied"
    },

    successTitle: function () {
      if (!this.result) return ''
      // Phase B: bulk-filter mode — always-202 async result; mode-aware title
      if (this.result.mode === 'bulk-filter') {
        var q = this.result.queued
        var s = q === 1 ? '' : 's'
        var bm = this.result.bulkMode
        if (bm === 'rate') {
          if (this.result.clearOverride) {
            // overridesToClear is the API's PROJECTION (locked targets counted when the
            // request was resolved), so the copy says "will be cleared", not "cleared".
            var c = this.result.overridesToClear
            if (c === null || c === undefined) {
              return 'Queued ' + q + ' rate change' + s + '; any rate overrides will be cleared — fees recalculating in the background.'
            }
            if (c >= 1) {
              return 'Queued ' + q + ' rate change' + s + '; ' + c + ' override' + (c === 1 ? '' : 's') + ' will be cleared — fees recalculating in the background.'
            }
            return 'Queued ' + q + ' rate change' + s + ' — no rate overrides to clear. Fees recalculating in the background.'
          }
          return 'Queued ' + q + ' rate change' + s + ' — fees recalculating in the background.'
        }
        if (bm === 'override') {
          return 'Queued ' + q + ' override' + s + ' — fees recalculating in the background.'
        }
        if (bm === 'override-clear') {
          return 'Queued ' + q + ' override removal' + s + ' — fees recalculating in the background.'
        }
        // Fallback (no bulkMode — legacy callers)
        return 'Queued ' + q + ' — fees recalculating in the background.'
      }
      var n = this.result.applied
      if (this.result.mode === 'rate') {
        if (this.result.clearOverride) {
          var cleared = this.result.overridesCleared || 0
          if (cleared >= 1) {
            return 'Applied ' + n + ' rate change' + (n === 1 ? '' : 's') + ' and cleared ' + cleared + ' rate override' + (cleared === 1 ? '' : 's') + '.'
          }
          return 'Applied ' + n + ' rate change' + (n === 1 ? '' : 's') + '. No rate overrides to clear.'
        }
        return 'Applied ' + n + ' rate change' + (n === 1 ? '' : 's') + '.'
      }
      if (this.result.mode === 'override-set') {
        return 'Applied ' + n + ' override' + (n === 1 ? '' : 's') + '.'
      }
      if (this.result.mode === 'override-clear') {
        return 'Cleared ' + n + ' override' + (n === 1 ? '' : 's') + '.'
      }
      return 'Applied ' + n + ' change' + (n === 1 ? '' : 's') + '.'
    }
  },

  methods: {
    // Key helper for the defensive failure list in bulk-filter mode.
    // Avoids inline string concatenation with single quotes in the template.
    bulkFilterFailureKey: function (f, idx) {
      return String(f.licensee_id || idx) + '-' + String(f.reason || '')
    },

    // Resolve the display name for the chosen rate, falling back gracefully.
    resolveRateName: function () {
      var rateSelect = this.$refs.rateSelect
      if (rateSelect && rateSelect.options) {
        var id = this.rateId
        var match = rateSelect.options.find(function (o) { return o.id === id })
        if (match) return match.name
      }
      return 'the selected rate'
    },

    // Resolve the version suffix string for the rate-assign confirm body.
    resolveVersionSuffix: function () {
      if (!this.versionId) return ' (latest version)'
      var vid = this.versionId
      var match = this.rateVersions.find(function (v) { return v.id === vid })
      if (match && match.version_no !== undefined) {
        return ' (version v' + match.version_no + ')'
      }
      return ' (the pinned version)'
    },

    onApplyVersionInput: function (val) {
      this.versionId = val
    },

    // On rate change: clear version, load versions, auto-pin is_current.
    onRateChange: function (rateId) {
      this.versionId = null
      this.rateVersions = []
      if (!rateId) return
      var self = this
      self.loadingVersions = true
      axios.get(self.getEndpoint('rates/' + rateId + '/versions'))
        .then(function (resp) {
          var data = (resp.data && resp.data.data) ? resp.data.data : (resp.data || [])
          self.rateVersions = Array.isArray(data) ? data : []
          // Auto-pin is_current
          var current = self.rateVersions.find(function (v) { return v.is_current })
          self.versionId = current ? current.id : null
          self.loadingVersions = false
        })
        .catch(function () {
          self.rateVersions = []
          self.versionId = null
          self.loadingVersions = false
        })
    },

    // ── XOL-653 part B: confirm sentences for "Also clear rate overrides" ───────
    // Counts are resolved BEFORE the dialog opens, so nothing changes inside an
    // open dialog. **Bold** is <strong> (the confirm renders HTML).

    // Explicit selection. N = selected rows, M = how many of them carry an override.
    explicitClearOverrideLine: function (N, M) {
      if (M > 0) {
        if (N === 1) {
          return 'This licensee has a rate override. Its manual fee will be removed, and ' + BILL_NEW_RATE
        }
        if (M === 1) {
          return '<strong>1</strong> of these licensees has a rate override. Its manual fee will be removed, and ' + BILL_NEW_RATE
        }
        return '<strong>' + M + '</strong> of these licensees have a rate override. Their manual fees will be removed, and ' + BILL_NEW_RATE
      }
      return N === 1
        ? 'This licensee has no rate override, so no manual fee will be removed.'
        : 'None of these licensees has a rate override, so no manual fee will be removed.'
    },

    // Select-all. total = overridden licensees matching the filter, or null when
    // the pre-flight failed. hasExclusions: the user de-selected some rows, which the
    // pre-flight count cannot subtract, so the number becomes "up to".
    selectAllClearOverrideLine: function (total, N, hasExclusions) {
      var keep = ' Licensees you de-selected keep their current rate and override.'
      var valid = total !== null && total !== undefined && total !== '' && isFinite(Number(total))
      if (!valid) {
        return 'Every matching licensee with a rate override will have its manual fee removed, and ' + BILL_NEW_RATE +
          " The number of overridden licensees couldn't be loaded." + (hasExclusions ? keep : '')
      }
      var K = Math.min(Number(total), N)
      if (K <= 0) {
        return 'None of the matching licensees has a rate override, so no manual fee will be removed.'
      }
      var line
      if (K === 1) {
        line = (hasExclusions ? 'At most <strong>1</strong> matching licensee has' : '<strong>1</strong> matching licensee has') +
          ' a rate override. Its manual fee will be removed, and ' + BILL_NEW_RATE
      } else {
        line = (hasExclusions ? 'Up to <strong>' + K + '</strong> matching licensees have' : '<strong>' + K + '</strong> matching licensees have') +
          ' a rate override. Their manual fees will be removed, and ' + BILL_NEW_RATE
      }
      return line + (hasExclusions ? keep : '')
    },

    // Pre-flight for select-all: how many of the matching licensees are overridden.
    // Resolves a number, or null on any failure — never rejects, never toasts (the
    // confirm degrades to a fallback sentence instead).
    countLockedMatching: function (filter) {
      var self = this
      return axios.get(self.getEndpoint('/licensees/search'), { params: lockedCountParams(filter || self.filter) })
        .then(function (resp) {
          var total = resp && resp.data ? resp.data.total : null
          return (total === null || total === undefined || total === '' || !isFinite(Number(total))) ? null : Number(total)
        })
        .catch(function () {
          return null
        })
    },

    confirmAndApply: function () {
      // Override amount commits on the input's change (blur). Clicking Apply blurs
      // the field first, so the just-typed value is in `overrideAmount` by now.
      // Validate it BEFORE the canApply gate so an empty/zero amount shows guidance
      // rather than silently proceeding.
      if (this.applyMode === 'override' && !(Number(this.overrideAmount) > 0)) {
        this.$message({ type: 'warning', message: 'Enter a manual fee greater than 0 before applying the override.' })
        return
      }

      if (!this.canApply) return
      // The pre-flight below is async: a second click must not start a second one.
      if (this.countingOverrides) return

      var self = this

      // XOL-653 part B — snapshot the choice NOW. Only this value drives the confirm
      // copy and the payload, so a toggle while the dialog is open (or while the
      // select-all pre-flight runs) cannot change what is sent. The gate is part of
      // the snapshot: a tick that is gated is never honoured.
      var clearOverrideAtClick = this.applyMode === 'rate' && this.clearOverride && !this.clearOverrideGated

      // Phase B — select-all mode: route to filter-mode POST /licensees/bulk-rate
      if (this.selectAllMatching) {
        var filterOrgName = (this.filter && this.filter.organisation_id)
          ? this.filter.organisation_id
          : ''
        // Try to resolve the org name from the first checked row (may be empty in
        // pure select-all with no row checked, but the banner requires at least the
        // page to be fully selected so licenseeRows[0] is reliable).
        var orgNameForAll = (self.licenseeRows[0] && self.licenseeRows[0].organisation && self.licenseeRows[0].organisation.name) || String(filterOrgName)
        var N = this.count
        var bulkMessage, bulkTitle, bulkConfirmText

        if (this.applyMode === 'override') {
          // Select-all override: mirror explicit override copy for all-matching
          var overrideAmt = this.formatMoney(this.overrideAmount, 2)
          bulkTitle = 'Set manual override'
          bulkConfirmText = 'Set override'
          bulkMessage = 'Set a manual fee of <strong>' + overrideAmt + '</strong> on all <strong>' + N + '</strong> matching licensees for <strong>' + orgNameForAll + '</strong>?' +
            '<br/>This locks each fee at the manual amount; the scheduled rate recalculation skips these licensees until the override is cleared.'
        } else {
          // Rate mode (default)
          var rateName = this.resolveRateName()
          bulkTitle = 'Apply rate change'
          bulkConfirmText = 'Apply rate change'
          bulkMessage = 'Apply <strong>' + rateName + '</strong> to all <strong>' + N + '</strong> matching licensees for <strong>' + orgNameForAll + '</strong>?' +
            '<br/>This replaces the current rate on every matching licensee and triggers a fee recalculation — fees recompute in the background.'
        }

        var selectAllMode = this.applyMode
        var hasExclusions = this.excludedIds.length > 0
        // Everything the POST will send is captured NOW, next to the numbers the
        // confirm shows. The pre-flight below is async and nothing is modal while it
        // runs, so reading live values later could send something other than what
        // the user was told (another rate, no exclusions, an edited filter).
        var snapshot = {
          rateId: this.rateId,
          versionId: this.versionId,
          excludedIds: this.excludedIds.slice(),
          filter: cloneParams(this.filter),
          overrideAmount: this.overrideAmount
        }
        var snapshotFilterJson = JSON.stringify(snapshot.filter)
        // True when the pre-flight result no longer describes what Apply would send.
        var selectionMoved = function () {
          return !!(self._isDestroyed ||
            !self.selectAllMatching ||
            self.applyMode !== selectAllMode ||
            self.rateId !== snapshot.rateId ||
            self.versionId !== snapshot.versionId ||
            !sameIds(self.excludedIds, snapshot.excludedIds) ||
            JSON.stringify(self.filter) !== snapshotFilterJson)
        }
        var openBulkConfirm = function (extraLine) {
          self.$confirm(bulkMessage + (extraLine ? '<br/>' + extraLine : ''), bulkTitle, {
            confirmButtonText: bulkConfirmText,
            cancelButtonText: 'Cancel',
            dangerouslyUseHTMLString: true,
            center: true,
            type: 'warning'
          }).then(function () {
            self.runBulkFilter(selectAllMode, { clearOverride: clearOverrideAtClick, snapshot: snapshot })
          }).catch(function () {
            // Confirm cancelled — no-op (the tick stays, so a retry repeats the intent)
          })
        }

        if (clearOverrideAtClick) {
          // How many matching licensees are overridden? Counted before the dialog
          // opens; the checkbox and mode toggle are disabled meanwhile.
          this.countingOverrides = true
          this.countLockedMatching(snapshot.filter).then(function (total) {
            self.countingOverrides = false
            // The selection moved (or the bar went away) while we counted: the
            // numbers are for a different apply. Open nothing, send nothing.
            if (selectionMoved()) return
            openBulkConfirm(self.selectAllClearOverrideLine(total, N, hasExclusions))
          })
          return
        }
        openBulkConfirm('')
        return
      }

      // Explicit (fan-out) path
      var orgName = (this.licenseeRows[0] && this.licenseeRows[0].organisation && this.licenseeRows[0].organisation.name) || ''
      var count = this.count
      var message, title, confirmText

      if (this.applyMode === 'rate') {
        var rateName2 = this.resolveRateName()
        var versionSuffix = this.resolveVersionSuffix()
        title = 'Apply rate change'
        confirmText = 'Apply rate change'
        message = 'Apply <strong>' + rateName2 + '</strong>' + versionSuffix +
          ' to <strong>' + orgName + '</strong> (' + count + ' licensee' + (count === 1 ? '' : 's') + ')?' +
          '<br/>This replaces the current rate on every selected licensee and triggers a fee recalculation — fees recompute in the background.'
        if (clearOverrideAtClick) {
          message += '<br/>' + this.explicitClearOverrideLine(count, this.selectedLockedCount)
        }
      } else {
        // override-set — formatMoney prepends settings.currency automatically
        var amount = this.formatMoney(this.overrideAmount, 2)
        title = 'Set manual override'
        confirmText = 'Set override'
        message = 'Set a manual fee of <strong>' + amount + '</strong> on <strong>' + orgName + '</strong> (' +
          count + ' licensee' + (count === 1 ? '' : 's') + ')?' +
          '<br/>This locks each fee at the manual amount; the scheduled rate recalculation skips these licensees until the override is cleared.'
      }

      this.$confirm(message, title, {
        confirmButtonText: confirmText,
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        center: true,
        type: 'warning'
      }).then(function () {
        self.runFanOut(self.applyMode === 'rate' ? 'rate' : 'override-set', { clearOverride: clearOverrideAtClick })
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    confirmAndClear: function () {
      if (!this.canClear) return

      var self = this
      var count = this.count
      var message, title, confirmText

      if (this.selectAllMatching) {
        // Select-all clear: route through filter-mode POST
        var filterOrgName = (this.filter && this.filter.organisation_id)
          ? this.filter.organisation_id
          : ''
        var orgNameForClear = (self.licenseeRows[0] && self.licenseeRows[0].organisation && self.licenseeRows[0].organisation.name) || String(filterOrgName)
        title = 'Clear override'
        confirmText = 'Clear override'
        message = 'Clear the manual override on all <strong>' + count + '</strong> matching licensees for <strong>' + orgNameForClear + '</strong>?' +
          '<br/>This returns them to the calculated (automatic) rate; fees recompute in the background — this may take a moment.'

        this.$confirm(message, title, {
          confirmButtonText: confirmText,
          cancelButtonText: 'Cancel',
          dangerouslyUseHTMLString: true,
          center: true,
          type: 'warning'
        }).then(function () {
          self.runBulkFilter('override-clear')
        }).catch(function () {
          // Confirm cancelled — no-op
        })
        return
      }

      // Explicit (fan-out) path
      var orgName = (this.licenseeRows[0] && this.licenseeRows[0].organisation && this.licenseeRows[0].organisation.name) || ''
      message = 'Clear the manual override on <strong>' + orgName + '</strong> (' +
        count + ' licensee' + (count === 1 ? '' : 's') + ')?' +
        '<br/>This returns ' + count + ' licensee' + (count === 1 ? '' : 's') +
        ' to the calculated (automatic) rate; fees recompute in the background — this may take a moment.'

      this.$confirm(message, 'Clear override', {
        confirmButtonText: 'Clear override',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        center: true,
        type: 'warning'
      }).then(function () {
        self.runFanOut('override-clear')
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    // Build the per-row request based on mode.
    // opts.clearOverride (rate mode only): one atomic flag on the same PUT that
    // assigns the rate — never a second request. It goes on EVERY selected row,
    // because the row's rate_locked can be stale; the API treats it as a no-op on
    // an unlocked licensee. The override modes never carry it.
    requestForRow: function (row, mode, opts) {
      var self = this
      if (mode === 'rate') {
        var payload = {
          rate_id: self.rateId,
          site_id: row.site && row.site.id,
          organisation_id: row.organisation && row.organisation.id,
          updated_by: self.userName()
        }
        // XOL-497: OMIT the key when no version is picked — do not send an
        // explicit null. This path PUTs to /licensees/{id}, the SINGLE-licensee
        // endpoint, where API XOL-507 refuses an explicit null with a 422 on a
        // rate change. The filter-mode POST below goes to the BULK endpoint,
        // whose $request->filled() treats null and '' as absent, so a null is
        // harmless there. Two endpoints, opposite semantics, one picker feeding
        // both — this branch previously sent `self.versionId || null`
        // unconditionally.
        //
        // Before #252 that succeeded by silently creating the rated-but-unpinned
        // row XOL-507 exists to prevent, so the 422 is correct and the payload
        // was wrong. Omitting makes the API auto-pin is_current, which is what
        // the confirm's "(latest version)" already promises.
        if (self.versionId) {
          payload.rate_version_id = self.versionId
        }
        if (opts && opts.clearOverride) {
          payload[CLEAR_OVERRIDE_KEY] = true
        }
        return axios.put(self.getEndpoint('/licensees/' + row.licensee_id), payload)
      }
      if (mode === 'override-set') {
        return axios.put(self.getEndpoint('/licensees/' + row.licensee_id + '/rate-override'), {
          currentRate: self.overrideAmount
        })
      }
      // override-clear
      return axios.delete(self.getEndpoint('/licensees/' + row.licensee_id + '/rate-override'))
    },

    runFanOut: function (mode, opts) {
      var self = this
      var rows = this.licenseeRows.slice()
      // XOL-653: only the rate mode ever clears overrides.
      var clearOverride = !!(mode === 'rate' && opts && opts.clearOverride)
      var succeeded = []
      var failed = []

      this.applying = true
      this.doneCount = 0
      this.result = null

      // Sequential fan-out with bounded concurrency = 1 (deterministic, avoids
      // flooding the server). The plan allows ≤ 3; sequential keeps error order
      // predictable and is simplest to reason about.
      var idx = 0

      function next () {
        if (idx >= rows.length) {
          self.result = {
            applied: succeeded.length,
            failed: failed.length,
            failures: failed,
            mode: mode,
            clearOverride: clearOverride,
            // Succeeded rows that had an override (the flag is atomic per row, so
            // these are exactly the overrides that were cleared).
            overridesCleared: clearOverride ? succeeded.filter(isLocked).length : 0
          }
          self.applying = false
          if (succeeded.length > 0) {
            self.$emit('applied', self.result)
            if (mode === 'rate') {
              self.rateId = null
              self.clearOverride = false
            }
            if (mode === 'override-set') self.overrideAmount = null
          }
          return
        }

        var row = rows[idx]
        idx++

        self.requestForRow(row, mode, opts)
          .then(function () {
            succeeded.push(row)
            self.doneCount++
            next()
          })
          .catch(function (err) {
            failed.push({ row: row, error: err })
            self.doneCount++
            next()
          })
      }

      next()
    },

    // Phase B — filter-mode apply: single POST /api/licensees/bulk-rate.
    // mode: 'rate' (default) | 'override' | 'override-clear'
    // Payload rules per mode:
    //   rate: {rate_id, organisation_id, filter} + rate_version_id (when pinned) + exclude_ids (when non-empty)
    //   override: {mode:'override', currentRate, organisation_id, filter} + exclude_ids (when non-empty); NO rate_id/version
    //   override-clear: {mode:'override-clear', organisation_id, filter} + exclude_ids (when non-empty); NO rate_id/currentRate/version
    // organisation_id goes at top level AND stays inside filter (see note below).
    // axios 0.15.3 resolves (does NOT reject) on 202 — handle in .then. 422 lands in .catch.
    runBulkFilter: function (mode, opts) {
      var self = this
      mode = mode || 'rate'

      // Build the filter payload. organisation_id goes at the TOP LEVEL (the
      // controller's required_with:filter + rate-belongs-to-org check read it
      // there). It also STAYS inside `filter`: the endpoint's `filter` rule is
      // `required_without:licensee_ids|array`, and Laravel's required treats an
      // empty array as absent — so a telco-only select-all (no other filters)
      // would 422 with an empty filter. Keeping organisation_id in `filter`
      // guarantees it is non-empty; applyLicenseeFilters has no organisation_id
      // handler so it is a no-op there (org scoping is separate). The filter then
      // mirrors exactly what /licensees/search received.
      //
      // opts.snapshot (select-all, captured at click) wins over the live props; the
      // fallback keeps every other caller as it was.
      var snap = (opts && opts.snapshot) || null
      var srcFilter = snap ? snap.filter : this.filter
      var srcRateId = snap ? snap.rateId : this.rateId
      var srcVersionId = snap ? snap.versionId : this.versionId
      var srcExcluded = snap ? snap.excludedIds : this.excludedIds
      var srcAmount = snap ? snap.overrideAmount : this.overrideAmount
      var filter = Object.assign({}, srcFilter)
      var orgId = srcFilter.organisation_id

      var payload

      if (mode === 'override') {
        // Override mode: send mode + currentRate; NO rate_id / rate_version_id
        payload = {
          mode: 'override',
          currentRate: Number(srcAmount),
          organisation_id: orgId,
          filter: filter
        }
      } else if (mode === 'override-clear') {
        // Override-clear mode: just mode; NO rate_id / currentRate / version
        payload = {
          mode: 'override-clear',
          organisation_id: orgId,
          filter: filter
        }
      } else {
        // Rate mode (default)
        payload = {
          rate_id: srcRateId,
          organisation_id: orgId,
          filter: filter
        }
        // Version pin (XOL-340 / #170): send the chosen version when one is picked.
        // Nothing picked → OMIT the key, so the API auto-pins the rate's current
        // published version.
        //
        // XOL-497 fold-in (Carlos, 2026-09-13): the placeholder used to read
        // "Latest (unversioned)", naming a state the product does not allow —
        // a licensee is always pinned to a PUBLISHED version. The label was
        // wrong; the behaviour was already right, and the distinction matters
        // if anyone revisits this.
        //
        // Unlike FinanceSharer's version select, `clearable` STAYS here.
        // Clearing this control does not unpin anyone: it omits the key, and
        // the API then pins `is_current` (RateVersion::currentForRate) or
        // fails the row with `rate_not_published` when the rate has no
        // published version. That is a legitimate choice — "give them whatever
        // is current" — and it is the default. FinanceSharer differed because
        // clearing there SENT an explicit null on a rate change, which API
        // XOL-507 now rejects with a 422; that is why clearable was removed
        // from that one and not from this one.
        //
        // NOTE: that reasoning holds for THIS branch only. requestForRow()
        // PUTs to the single-licensee endpoint and had to be fixed separately
        // — see the comment there. "Clearable is safe on the bulk picker" is
        // true of the endpoint, not of every request the screen makes.
        if (this.versionPinAvailable && srcVersionId) {
          payload.rate_version_id = srcVersionId
        }
        // XOL-653: one atomic flag on the rate write. Rate branch ONLY — the
        // override and override-clear payloads above never carry it.
        if (opts && opts.clearOverride) {
          payload[CLEAR_OVERRIDE_KEY] = true
        }
      }

      // XOL-321 Phase B — include exclude_ids only when non-empty. Applies to all modes.
      if (srcExcluded.length) {
        payload.exclude_ids = srcExcluded.slice()
      }

      var clearOverride = !!(mode === 'rate' && opts && opts.clearOverride)

      this.applying = true
      this.result = null

      axios.post(self.getEndpoint('/licensees/bulk-rate'), payload)
        .then(function (resp) {
          // Always 202 — axios resolves here (default validateStatus: 200-299)
          self.result = {
            mode: 'bulk-filter',
            bulkMode: mode,
            queued: resp.data.queued,
            failures: resp.data.failures || [],
            clearOverride: clearOverride,
            // The API's projection of how many locked targets the flag will clear
            // (counted when the request was resolved; 0 when the flag was off).
            overridesToClear: (resp.data[OVERRIDES_TO_CLEAR_KEY] === undefined ? null : resp.data[OVERRIDES_TO_CLEAR_KEY])
          }
          self.applying = false
          // Reset the relevant input per mode
          if (mode === 'rate') {
            self.rateId = null
            self.clearOverride = false
          }
          if (mode === 'override') self.overrideAmount = null
          // override-clear: no input to reset
          self.$emit('applied', self.result)
        })
        .catch(function (err) {
          self.applying = false
          var data = (err.response && err.response.data) || {}
          if (err.response && err.response.status === 422) {
            // Guards that return {error: …}: the rate-not-in-organisation case gets
            // a friendly telco message; the rate-version-mismatch case (and any
            // other {error}) is surfaced verbatim — it's already readable and the
            // telco copy would mislead. Validation errors fall back to {message}.
            var serverError = data.error || ''
            var message
            if (/organisation/i.test(serverError)) {
              message = "That rate isn't available for this telco. Pick a rate that belongs to the selected telco and try again."
            } else if (serverError) {
              message = serverError
            } else {
              message = data.message || "Couldn't queue the rate change. Check your filters and try again."
            }
            self.result = {
              mode: 'bulk-filter-error-422',
              error: message
            }
          } else {
            self.result = {
              mode: 'bulk-filter-error',
              error: "Couldn't queue the rate change. Try again in a moment."
            }
          }
        })
    }
  },

  watch: {
    // Switching mode clears the result alert so a stale banner does not
    // sit under the new mode's controls.
    applyMode: function () {
      this.result = null
      // The tick belongs to rate mode; it never carries over to another mode.
      this.clearOverride = false
    },

    // When the gate turns on (e.g. the user enters select-all before the API half
    // is live), the tick is dropped: a disabled tick that is still sent would be a
    // money-path bug.
    clearOverrideGated: function (on) {
      if (on) this.clearOverride = false
    },

    rateId: function (val, oldVal) {
      if (val === oldVal) return
      this.onRateChange(val)
    },

    // Entering select-all snaps to rate mode as the safe default.
    // Override is now enabled in select-all (XOL-340 live), but defaulting to rate
    // is the least-surprising behaviour when the user first clicks "Select all N".
    // The user can switch to override after entering select-all mode.
    selectAllMatching: function (on) {
      if (on) this.applyMode = 'rate'
    }
  }
}
</script>

<style scoped>
.bulk-rate-assign {
  background: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.bulk-rate-assign__bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.bulk-rate-assign__count {
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}

.bulk-rate-assign__org-alert {
  flex: 1;
  min-width: 260px;
}

.bulk-rate-assign__mode {
  flex: 0 0 auto;
}

.bulk-rate-assign__picker {
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}

.bulk-rate-assign__version {
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}

.bulk-rate-assign__apply-btn {
  white-space: nowrap;
}

/* XOL-653: the checkbox and its HelpTip stay together as one unit when the bar wraps. */
.bulk-rate-assign__clear-override {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  white-space: nowrap;
}

.bulk-rate-assign__progress {
  margin-top: 8px;
  color: #909399;
  font-size: 13px;
}

.bulk-rate-assign__result-alert {
  margin-top: 10px;
}

.bulk-rate-assign__failure-list {
  margin-top: 4px;
  max-height: 160px;
  overflow-y: auto;
  font-size: 12px;
}

.bulk-rate-assign__failure-item {
  padding: 2px 0;
  color: #606266;
}
</style>
